import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, AlertCircle, ArrowLeft, CheckCircle, RefreshCw, Eye, EyeOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { SyncLogoWithText } from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'newPassword' | 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState(emailParam || '');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [codeExpiresIn, setCodeExpiresIn] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  
  const codeInputRefs = useRef([]);
  const navigate = useNavigate();
  
  // Countdown timer for code expiry
  useEffect(() => {
    if (codeExpiresIn > 0) {
      const timer = setInterval(() => {
        setCodeExpiresIn(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [codeExpiresIn]);
  
  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');
    
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };
  
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };
  
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newCode = [...verificationCode];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setVerificationCode(newCode);
      const nextEmptyIndex = newCode.findIndex(c => !c);
      if (nextEmptyIndex !== -1) {
        codeInputRefs.current[nextEmptyIndex]?.focus();
      } else {
        codeInputRefs.current[5]?.focus();
      }
    }
  };
  
  // Step 1: Request password reset code
  const requestReset = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/request-password-reset`, { email });
      setStep('code');
      setCodeExpiresIn(response.data.expires_in_minutes * 60);
      setVerificationCode(['', '', '', '', '', '']);
      toast.success('Reset code sent! Check your email.');
      
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send reset code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 2: Verify code and proceed to new password
  const verifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    // Move to new password step
    setStep('newPassword');
    setError('');
  };
  
  // Step 3: Set new password
  const resetPassword = async () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code: verificationCode.join(''),
        new_password: newPassword
      });
      
      setStep('success');
      toast.success('Password reset successfully!');
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      setError(message);
      toast.error(message);
      
      // If code is invalid/expired, go back to code step
      if (message.toLowerCase().includes('code') || message.toLowerCase().includes('expired')) {
        setStep('code');
        setVerificationCode(['', '', '', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Resend code
  const resendCode = async () => {
    setResendDisabled(true);
    await requestReset();
    
    setTimeout(() => {
      setResendDisabled(false);
    }, 60000);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => step === 'email' ? navigate('/auth') : setStep('email')}
            className="text-gray-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 'email' ? 'Back to Login' : 'Start Over'}
          </Button>
        </div>
        
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <SyncLogoWithText variant="dark" size="large" />
          </div>
          <p className="text-gray-400 mt-2">Password Recovery</p>
        </div>

        <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
          {/* Step 1: Enter Email */}
          {step === 'email' && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-white text-center">
                  Forgot Password?
                </CardTitle>
                <CardDescription className="text-gray-400 text-center">
                  Enter your email and we'll send you a reset code
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {error && (
                  <Alert className="bg-red-500/20 border-red-500/50 text-red-100">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
                
                <Button
                  onClick={requestReset}
                  disabled={loading || !email}
                  className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                  data-testid="send-reset-code-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Reset Code
                    </>
                  )}
                </Button>
              </CardContent>
            </>
          )}
          
          {/* Step 2: Enter Code */}
          {step === 'code' && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-white text-center">
                  Enter Reset Code
                </CardTitle>
                <CardDescription className="text-gray-400 text-center">
                  We sent a 6-digit code to <span className="text-[#5A9BD5]">{email}</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {error && (
                  <Alert className="bg-red-500/20 border-red-500/50 text-red-100">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                {/* Code Input */}
                <div className="flex justify-center gap-2">
                  {verificationCode.map((digit, index) => (
                    <Input
                      key={index}
                      ref={el => codeInputRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      onPaste={index === 0 ? handleCodePaste : undefined}
                      className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border-white/20 text-white focus:border-[#ef4444]"
                      data-testid={`reset-code-${index}`}
                    />
                  ))}
                </div>
                
                {/* Timer */}
                {codeExpiresIn > 0 && (
                  <p className="text-center text-sm text-gray-400">
                    Code expires in{' '}
                    <span className="text-[#ef4444] font-semibold">
                      {Math.floor(codeExpiresIn / 60)}:{(codeExpiresIn % 60).toString().padStart(2, '0')}
                    </span>
                  </p>
                )}
                
                <Button
                  onClick={verifyCode}
                  disabled={loading || verificationCode.join('').length !== 6}
                  className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                  data-testid="verify-reset-code-button"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Continue
                </Button>
                
                {/* Resend */}
                <div className="text-center">
                  <p className="text-sm text-gray-400 mb-2">Didn't receive the code?</p>
                  <Button
                    variant="ghost"
                    onClick={resendCode}
                    disabled={resendDisabled || loading}
                    className="text-[#5A9BD5] hover:text-[#6AABF8]"
                    data-testid="resend-reset-code-button"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${resendDisabled ? 'animate-spin' : ''}`} />
                    {resendDisabled ? 'Please wait...' : 'Resend Code'}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
          
          {/* Step 3: New Password */}
          {step === 'newPassword' && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-white text-center">
                  Set New Password
                </CardTitle>
                <CardDescription className="text-gray-400 text-center">
                  Create a strong password for your account
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {error && (
                  <Alert className="bg-red-500/20 border-red-500/50 text-red-100">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-gray-300">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                      className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-500 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Minimum 8 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#5A9BD5]"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-gray-500 hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  onClick={resetPassword}
                  disabled={loading || !newPassword || !confirmPassword}
                  className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                  data-testid="reset-password-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <KeyRound className="mr-2 h-4 w-4" />
                      Reset Password
                    </>
                  )}
                </Button>
              </CardContent>
            </>
          )}
          
          {/* Step 4: Success */}
          {step === 'success' && (
            <>
              <CardHeader className="space-y-1">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-white text-center">
                  Password Reset!
                </CardTitle>
                <CardDescription className="text-gray-400 text-center">
                  Your password has been successfully reset
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                  data-testid="go-to-login-button"
                >
                  Go to Login
                </Button>
              </CardContent>
            </>
          )}
        </Card>

        <p className="mt-6 text-center text-sm text-gray-500">
          Remember your password?{' '}
          <button
            onClick={() => navigate('/auth')}
            className="text-[#5A9BD5] hover:text-[#6AABF8] hover:underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
